import { useCallback, useState } from 'react';

import { Avatar, Radio, RadioGroup, Select, Spacer, Typography } from '@aircall/tractor';
import { Popup } from '@components/Popup';
import { SELECT_ALL_NUMBERS_PARAM_VALUE } from '@constants/integrations.constants';
import { Gap } from '@dashboard/library';
import { ApplicationsListQuery_getApplications } from '@generated/ApplicationsListQuery';
import { AuthorizedApplicationQuery_getRestAuthorizedApplication } from '@generated/AuthorizedApplicationQuery';
import { SearchLinesQuery_searchLines_items as Line } from '@generated/SearchLinesQuery';
import { formatInternational } from '@helpers/phoneNumber.helpers';
import { useNumbersList } from '@hooks/useNumbersList';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'usehooks-ts';

interface PopupAuthorizationProps {
  allowAll?: boolean;
  application:
    | ApplicationsListQuery_getApplications
    | AuthorizedApplicationQuery_getRestAuthorizedApplication;
  onSubmit: (lines: Line[] | typeof SELECT_ALL_NUMBERS_PARAM_VALUE) => Promise<void>;
  onSkip: () => void;
}

export function PopupSelectNumbers({
  allowAll = false,
  application,
  onSubmit,
  onSkip,
}: PopupAuthorizationProps) {
  const { t } = useTranslation();
  const dataTest = 'popup-select-numbers';
  const { logo, name } = application;

  const [selectAllNumbers, setSelectAllNumbers] = useState<boolean>(allowAll);
  const [selectedLinesIds, setSelectedLinesIds] = useState<string[]>([]);
  const [searchText, setSearchText] = useState('');
  const debouncedSearchText: string = useDebounce<string>(searchText, 500);
  const { lines, loading: linesLoading } = useNumbersList({
    searchTerm: debouncedSearchText,
  });

  const handleSelectSearch = useCallback((text: string) => {
    setSearchText(text);
  }, []);

  const handleSelectChange = useCallback((values: { label: string; value: string }[]) => {
    const _selectedLinesIds = values.map((item) => item.value);
    setSelectedLinesIds(_selectedLinesIds);
  }, []);

  const handleSubmit = useCallback(() => {
    if (selectAllNumbers) {
      return onSubmit(SELECT_ALL_NUMBERS_PARAM_VALUE);
    }

    /* istanbul ignore next */
    const selectedLines = lines?.items?.filter((line) => selectedLinesIds.includes(line.ID)) || [];

    return onSubmit(selectedLines);
  }, [lines, onSubmit, selectAllNumbers, selectedLinesIds]);

  const linesOptions =
    lines?.items?.map((line) => ({
      value: line.ID,
      label: `${line.name} (${formatInternational(line.phoneNumber)})`,
    })) || [];

  return (
    <Popup
      submitText={t('integration_flow.select_numbers.add_numbers')}
      onSubmit={handleSubmit}
      cancelText={t('integration_flow.select_numbers.skip')}
      onCancel={onSkip}
      data-test={dataTest}
    >
      <Spacer direction='vertical' alignItems='center' space='m'>
        <Spacer space='xl'>
          <Avatar size='xLarge' src='https://cdn.aircall.io/aircall-logo-squared.png' />
          {logo && <Avatar data-test={`${dataTest}-app-logo`} size='xLarge' src={logo} />}
        </Spacer>
        <Gap flexDirection='column' alignItems='center' gap='m'>
          <Gap flexDirection='column' alignItems='center' gap='xxxs'>
            <Typography variant='subheading'>
              {t('integration_flow.authorization.popup.select_numbers.title', {
                applicationName: name,
              })}
            </Typography>
            <Typography variant='body'>
              {t('integration_flow.authorization.popup.select_numbers.subtitle', {
                applicationName: name,
              })}
            </Typography>
          </Gap>

          {allowAll && (
            <RadioGroup
              defaultValue={selectAllNumbers}
              direction='horizontal'
              space='l'
              onChange={(value) => setSelectAllNumbers(value)}
            >
              <Radio value data-test='radio-select-all'>
                {t('integration_flow.select_numbers.select_all_numbers')}
              </Radio>
              <Radio value={false} data-test='radio-select-specific'>
                {t('integration_flow.select_numbers.select_specific_numbers')}
              </Radio>
            </RadioGroup>
          )}

          {!selectAllNumbers && (
            <Select
              data-test='popup-select-numbers-select'
              name='popup-select-numbers-select'
              mode='multiple'
              placeholder={t('integration_flow.select_numbers.sub_title')}
              width='100%'
              loading={linesLoading}
              onSearch={handleSelectSearch}
              onChange={handleSelectChange}
              options={linesOptions}
              searchable
            />
          )}
        </Gap>
      </Spacer>
    </Popup>
  );
}
